import React, { useState } from 'react';
import { Package, User, Lock, ArrowRight } from 'lucide-react';
import { useTheme } from '../core/ui/ThemeContext';
import { useLanguage } from '../core/i18n/LanguageContext';
import MfaStep from '../core/auth/MfaStep';
import MfaEnroll from '../core/auth/MfaEnroll';

const LoginView = ({ onLogin, setupMfa, completeMfa, ssoFailed, logo, siteTitle }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfa, setMfa] = useState(null); // { enrolled, mfaToken } nach erfolgreichem Passwort-Schritt
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const handleSubmit = async () => {
    const username = document.getElementById('username').value.trim();
    const password = document.getElementById('password').value;

    if (!username || !password) {
      setError(t('login.fillAllFields'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await onLogin({ username, password });
      if (result?.mfa) setMfa(result.mfa);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const backToPassword = (message) => {
    setMfa(null);
    setError(message);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSubmit();
    }
  };

  return (
    <div className="relative group">
      {/* Liquid Glass Background with Dynamic Layers */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/40 via-gray-800/30 to-gray-900/40 backdrop-blur-3xl border border-white/20 shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
        : 'glass-light-card'
      } rounded-3xl`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-blue-600/15 via-transparent to-purple-600/15'
        : ''
      } rounded-3xl`}></div>

      {/* Specular Highlights - Dynamic Glass Reflection */}
      <div className={`absolute top-1/3 right-8 w-16 h-16 bg-gradient-to-br from-white/20 to-transparent rounded-full blur-xl group-hover:scale-110 transition-transform duration-500 ${isDarkMode ? '' : 'hidden'}`}></div>

      {/* Floating Glass Particles */}
      <div className={`absolute top-12 right-16 w-2 h-2 bg-white/40 rounded-full animate-pulse ${isDarkMode ? '' : 'hidden'}`}></div>
      <div className={`absolute bottom-20 left-12 w-1 h-1 bg-blue-300/60 rounded-full animate-ping ${isDarkMode ? '' : 'hidden'}`}></div>
      <div className={`absolute top-1/2 left-8 w-1.5 h-1.5 bg-purple-300/50 rounded-full animate-bounce ${isDarkMode ? '' : 'hidden'}`}></div>

      {/* Main Glass Container */}
      <div className={`relative ${isDarkMode
        ? 'bg-gray-900/20 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.3)] hover:shadow-[0_12px_40px_rgba(0,0,0,0.7)] hover:scale-[1.02] hover:border-white/40'
        : 'bg-transparent'
      } rounded-3xl p-8 w-full max-w-md overflow-hidden transition-all duration-500`}>

      <div className="relative z-10">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="relative mb-6">
            {/* Liquid Glass Logo Container */}
            <div className="relative group/logo">
              {/* Glass Reflection Layer */}
              <div className={`absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-3xl blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>

              {/* Main Logo Glass */}
              <div className={`relative ${isDarkMode
                ? 'bg-gradient-to-br from-slate-600/60 via-gray-600/50 to-slate-700/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20 hover:shadow-[0_12px_40px_rgba(0,0,0,0.4)]'
                : 'bg-brand/10'
              } w-20 h-20 rounded-3xl flex items-center justify-center mx-auto transform hover:scale-105 transition-all duration-500`}>
                {logo && logo !== 'null' && logo !== '' ? (
                  <img
                    src={logo}
                    alt="Portal Logo"
                    className="w-16 h-auto max-h-14 object-contain drop-shadow-lg relative z-10"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <Package
                  className={`w-10 h-10 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'} relative z-10`}
                  style={{ display: (logo && logo !== 'null' && logo !== '') ? 'none' : 'block' }}
                />

                {/* Inner Glass Highlight */}
                <div className={`absolute top-2 left-2 w-4 h-4 bg-white/40 rounded-full blur-sm group-hover/logo:scale-110 transition-transform duration-300 ${isDarkMode ? '' : 'hidden'}`}></div>
              </div>
            </div>
          </div>

          {/* Title */}
          <h1 className={`text-3xl font-bold ${isDarkMode
            ? 'bg-gradient-to-r from-white via-blue-200 to-white bg-clip-text text-transparent drop-shadow-lg'
            : 'text-slate-900'
          } mb-2`}>
            {siteTitle}
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
            {t('login.subtitle')}
          </p>

          {/* Divider */}
          <div className="mt-6 mb-8 relative">
            <div className={`h-px bg-gradient-to-r from-transparent ${isDarkMode ? 'via-white/40' : 'via-slate-200'} to-transparent`}></div>
            <div className={`absolute top-0 left-1/2 transform -translate-x-1/2 w-16 h-px bg-gradient-to-r from-blue-400/60 to-purple-400/60 blur-sm ${isDarkMode ? '' : 'hidden'}`}></div>
          </div>
        </div>

        {/* Form Section */}
        <div className="space-y-6">
          {(error || ssoFailed) && (
            <div className={`relative ${isDarkMode
              ? 'bg-red-500/10 backdrop-blur-xl border border-red-300/30 shadow-[0_8px_32px_rgba(239,68,68,0.2)]'
              : 'bg-red-50 border border-red-200'
            } rounded-2xl p-4`}>
              <div className={`absolute inset-0 bg-gradient-to-r from-red-400/10 to-pink-400/10 rounded-2xl ${isDarkMode ? '' : 'hidden'}`}></div>
              <p className={`${isDarkMode ? 'text-red-200' : 'text-red-700'} text-sm font-medium flex items-center relative z-10`}>
                <div className="w-2 h-2 bg-red-400/80 rounded-full mr-2 animate-pulse"></div>
                {error || t('login.ssoFailed')}
              </p>
            </div>
          )}

          {mfa ? (mfa.enrolled ? (
            <MfaStep mfaToken={mfa.mfaToken} completeMfa={completeMfa} setError={setError} onBack={backToPassword} />
          ) : (
            <MfaEnroll mfaToken={mfa.mfaToken} setupMfa={setupMfa} completeMfa={completeMfa} setError={setError} onBack={backToPassword} />
          )) : (<>
          {/* Username Field */}
          <div className="relative group/input">
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-700'} mb-3`}>{t('login.username')}</label>
            <div className="relative">
              {/* Glass Input Background */}
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-800/30 backdrop-blur-xl border border-white/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}></div>

              {/* Icon Container */}
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                <User className={`w-5 h-5 ${isDarkMode ? 'text-white/60 group-focus-within/input:text-blue-300/80' : 'text-gray-500 group-focus-within/input:text-brand'} transition-colors duration-300`} />
              </div>

              <input
                type="text"
                id="username"
                disabled={isLoading}
                onKeyPress={handleKeyPress}
                className={`relative w-full pl-12 pr-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? 'focus:ring-blue-400/50' : 'focus:ring-[#2563EB]/40'} focus:outline-none transition-all duration-300 disabled:opacity-50 ${isDarkMode
                  ? 'text-white placeholder-white/60 focus:shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                  : 'text-gray-800 placeholder-gray-400'
                }`}
                placeholder={t('login.username')}
              />

              {/* Focus Highlight */}
              <div className="absolute inset-0 rounded-2xl border border-transparent group-focus-within/input:border-blue-400/50 transition-all duration-300 pointer-events-none"></div>
            </div>
          </div>

          {/* Password Field */}
          <div className="relative group/input">
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-700'} mb-3`}>{t('login.password')}</label>
            <div className="relative">
              {/* Glass Input Background */}
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-800/30 backdrop-blur-xl border border-white/30 shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}></div>

              {/* Icon Container */}
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10">
                <Lock className={`w-5 h-5 ${isDarkMode ? 'text-white/60 group-focus-within/input:text-blue-300/80' : 'text-gray-500 group-focus-within/input:text-brand'} transition-colors duration-300`} />
              </div>

              <input
                type="password"
                id="password"
                disabled={isLoading}
                onKeyPress={handleKeyPress}
                className={`relative w-full pl-12 pr-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? 'focus:ring-blue-400/50' : 'focus:ring-[#2563EB]/40'} focus:outline-none transition-all duration-300 disabled:opacity-50 ${isDarkMode
                  ? 'text-white placeholder-white/60 focus:shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                  : 'text-gray-800 placeholder-gray-400'
                }`}
                placeholder={t('login.password')}
              />

              {/* Focus Highlight */}
              <div className="absolute inset-0 rounded-2xl border border-transparent group-focus-within/input:border-blue-400/50 transition-all duration-300 pointer-events-none"></div>
            </div>
          </div>

          {/* Submit Button — Blauer CTA (Stitch-Stil) */}
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full bg-brand hover:bg-[#1D4ED8] text-white font-semibold py-3.5 rounded-xl shadow-lg shadow-blue-600/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <span className="w-5 h-5 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
                <span>{t('login.submitting')}</span>
              </>
            ) : (
              <>
                <span>{t('login.submit')}</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
          </>)}

        </div>
      </div>
      </div>
    </div>
  );
};

export default LoginView;
